import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import OccupantPicker from "../OccupantPicker";
import { useLanguage } from "../../i18n/LanguageContext";
import { HOURS, toISODate } from "../../utils/calendar";

const fmtHour = (h) => `${String(h).padStart(2, "0")}:00`;
const todayISO = toISODate(new Date());

/**
 * `area` viene fijo (elegida antes de abrir este diálogo). Si se pasa
 * `existing` (una reservación ya guardada), el diálogo entra en modo
 * edición: precarga sus datos y muestra el botón de eliminar.
 */
export default function BookingDialog({ open, area, date, initialHour, existing, onClose, onSave, onDelete }) {
  const { t } = useLanguage();
  const [occupant, setOccupant] = useState(null);
  const [bookingDate, setBookingDate] = useState(date || todayISO);
  const [startHour, setStartHour] = useState(initialHour ?? HOURS[0]);
  const [endHour, setEndHour] = useState((initialHour ?? HOURS[0]) + 1);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setOccupant(existing.occupantId || null);
      setBookingDate(existing.date);
      setStartHour(existing.startHour ?? HOURS[0]);
      setEndHour(existing.endHour ?? HOURS[0] + 1);
    } else {
      setOccupant(null);
      setBookingDate(date || todayISO);
      const sh = initialHour ?? HOURS[0];
      setStartHour(sh);
      setEndHour(Math.min(sh + 1, HOURS[HOURS.length - 1] + 1));
    }
    setError("");
  }, [open, date, initialHour, existing]);

  if (!area) return null;
  const isFullDay = area.fullDayOnly;

  const handleSubmit = async () => {
    if (!occupant) return setError(t("areas.errNoOccupant"));
    if (bookingDate < todayISO) return setError(t("areas.errPastDate"));
    if (!isFullDay && startHour >= endHour) return setError(t("areas.errBadRange"));
    setSaving(true);
    setError("");
    try {
      await onSave({
        areaId: area._id,
        occupantId: occupant._id,
        occupant,
        date: bookingDate,
        fullDay: isFullDay,
        startHour: isFullDay ? null : startHour,
        endHour: isFullDay ? null : endHour,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || t("areas.errGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(existing._id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || t("areas.errGeneric"));
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {existing ? t("areas.editBooking") : t("areas.newBooking")}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <EventAvailableIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600}>
            {area.name}
          </Typography>
        </Stack>

        <Stack spacing={2}>
          <OccupantPicker value={occupant} onChange={setOccupant} />

          <TextField
            label={t("areas.date")}
            type="date"
            value={bookingDate}
            onChange={(e) => setBookingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: todayISO }}
            fullWidth
          />

          {isFullDay ? (
            <Alert severity="info" icon={false}>
              {t("areas.fullDayNotice")}
            </Alert>
          ) : (
            <Stack direction="row" spacing={2}>
              <TextField select label={t("areas.startTime")} value={startHour} onChange={(e) => setStartHour(Number(e.target.value))} fullWidth>
                {HOURS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {fmtHour(h)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select label={t("areas.endTime")} value={endHour} onChange={(e) => setEndHour(Number(e.target.value))} fullWidth>
                {HOURS.map((h) => (
                  <MenuItem key={h + 1} value={h + 1}>
                    {fmtHour(h + 1)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </Stack>

        {occupant && !existing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t("areas.notifyInfo", occupant.altEmail || occupant.email)}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" startIcon={<CheckIcon />} onClick={handleSubmit} disabled={saving || deleting}>
          {saving ? t("common.saving") : existing ? t("common.save") : t("areas.confirmBooking")}
        </Button>
        {existing && (
          <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={handleDelete} disabled={saving || deleting} sx={{ ml: "auto" }}>
            {t("common.delete")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
